import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../App';
import { useAuth } from '../../AuthContext';
import { t } from '../../i18n';
import { X, LayoutDashboard, Trophy, CreditCard, Dumbbell, BarChart3, LogIn, LogOut, User, Users, Swords, HandCoins, ShieldCheck, PenLine, ShoppingBag, Flame, Coins, BookOpen, Gamepad2, UserPlus, Trash2, PawPrint } from 'lucide-react';

export default function Sidebar({ open, onClose, gData }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { user, logout } = useAuth();

  const plan = user?.plan || 'free';
  const isEN = language === 'en';
  const hasClassAccess = plan === 'pro' || plan === 'school';
  const hasTournamentAccess = plan === 'school';
  const isAdmin = user?.email === 'imafiqwork@gmail.com';

  const level = gData?.level || 1;
  const rank = gData?.rank || {};
  const coins = gData?.coins || 0;
  const streak = gData?.current_streak || 0;

  const menuItems = [
    { icon: Gamepad2, label: isEN ? 'Play' : 'Main', path: '/' },
    { icon: BookOpen, label: isEN ? 'Guide' : 'Panduan', path: '/guide' },
    ...(hasClassAccess ? [{ icon: Users, label: isEN ? 'My Classes' : 'Kelas Saya', path: '/classes' }] : []),
    ...(hasTournamentAccess ? [{ icon: Swords, labelKey: 'tournaments', path: '/tournaments' }] : []),
    ...(plan === 'school' ? [{ icon: UserPlus, label: isEN ? 'Teacher Accounts' : 'Akaun Guru', path: '/school-teachers' }] : []),
    { icon: BarChart3, labelKey: 'progressDashboard', path: '/progress' },
    { icon: Trophy, labelKey: 'leaderboard', path: '/leaderboard' },
    { icon: ShoppingBag, label: isEN ? 'Shop' : 'Kedai', path: '/shop' },
    { icon: HandCoins, label: isEN ? 'Affiliate' : 'Affiliate', path: '/affiliate' },
    { icon: CreditCard, labelKey: 'pricing', path: '/pricing' },
    ...(isAdmin ? [{ icon: ShieldCheck, label: 'Admin', path: '/admin' }] : []),
  ];

  const handleNav = (path) => { navigate(path); onClose(); };
  const planColors = { free: '#6b7280', basic: '#2196F3', pro: '#E85D3A', school: '#009688' };

  return (
    <>
      {open && <div className="sidebar-overlay" onClick={onClose} data-testid="sidebar-overlay" />}
      <div className={`sidebar ${open ? 'sidebar-open' : ''}`} data-testid="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/pantass-logo.jpg" alt="PANTASS" style={{ width: 32, height: 32, borderRadius: 8 }} />
            <span className="sidebar-logo-text">PANTASS</span>
          </div>
          <button className="sidebar-close" onClick={onClose} aria-label="Close menu"><X size={22} /></button>
        </div>

        {/* User Info with Gamification */}
        {user ? (
          <div className="sidebar-user">
            <div className="sidebar-user-avatar" style={{ borderColor: rank.color || '#6b7280' }}>
              <span style={{ fontSize: '18px' }}>{rank.icon || '🌱'}</span>
            </div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user.name}</div>
              <div className="sidebar-user-meta">
                <span className="sidebar-user-plan" style={{ background: planColors[user.plan] || '#6b7280' }}>
                  {(user.plan || 'free').toUpperCase()}
                </span>
                {gData && (
                  <span className="sidebar-user-level" style={{ background: rank.color || '#9ca3af' }}>
                    Lv.{level}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="sidebar-login-prompt">
            <button className="sidebar-login-btn" onClick={() => handleNav('/login')}>
              <LogIn size={18} /> {t('loginBtn', language)}
            </button>
          </div>
        )}

        {/* Gamification Quick Stats */}
        {user && gData && (
          <div className="sidebar-gami-stats" data-testid="sidebar-gami-stats">
            <div className="sidebar-gami-item">
              <Flame size={16} color="#f59e0b" />
              <span>{streak} {isEN ? 'streak' : 'hari'}</span>
            </div>
            <div className="sidebar-gami-item" onClick={() => handleNav('/shop')} style={{ cursor: 'pointer' }}>
              <Coins size={16} color="#eab308" />
              <span>{coins}</span>
            </div>
          </div>
        )}

        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button key={item.path} className={`sidebar-nav-item ${isActive ? 'sidebar-nav-active' : ''}`}
                onClick={() => handleNav(item.path)} data-testid={`sidebar-nav-${item.labelKey || item.label}`}>
                <Icon size={20} /><span>{item.labelKey ? t(item.labelKey, language) : item.label}</span>
              </button>
            );
          })}
        </nav>

        {user && (
          <div className="sidebar-footer">
            <button className="sidebar-nav-item" onClick={() => handleNav('/pet')} style={{ width: '100%', justifyContent: 'flex-start', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', color: '#94a3b8', fontSize: 14 }}>
              <PawPrint size={18} /> {isEN ? 'My Pet' : 'Pet Saya'}
            </button>
            <button className="sidebar-logout-btn" onClick={() => { logout(); handleNav('/'); }}>
              <LogOut size={18} /> {t('logoutBtn', language)}
            </button>
            <button data-testid="delete-account-btn" onClick={() => handleNav('/settings/delete-account')}
              style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', color: '#475569', fontSize: 12, marginTop: 8 }}>
              <Trash2 size={14} /> {isEN ? 'Delete Account' : 'Padam Akaun'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
